/**
 * CameraBroadcaster — go live from the camera in front of you.
 *
 * Used by the church Go Live screen, the Faith Works TV studio and an online
 * event's live session: the record each of them keeps is different, the act of
 * broadcasting is not, so this is one component with two callbacks.
 *
 * The record is created BEFORE the camera connects, because the server clears
 * any stale room when it starts a broadcast — connecting first had the server
 * kick the broadcaster straight back out again.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Camera, Gauge, Loader2, Radio, Square, SwitchCamera, Users } from 'lucide-react';
import { useLiveKitPublisher } from '@/lib/liveKit';
import { CameraTrouble } from '@/components/live/CameraTrouble';
import { PreFlightCheck } from '@/components/live/PreFlightCheck';

export const CameraBroadcaster = ({
  scope, ident, titleLabel = 'What are you streaming?',
  titlePlaceholder = 'Sunday Morning Service', note = '',
  onStart, onStop, onLive, onStopped,
}) => {
  const [title, setTitle] = useState('');
  const [cameraOn, setCameraOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const pub = useLiveKitPublisher(scope, ident);
  const live = pub.status === 'live';

  const start = async () => {
    if (!cameraOn && !(await pub.openCamera())) return;
    setCameraOn(true);
    setBusy(true);
    try {
      if (onStart && (await onStart(title.trim())) === false) return;
      if (!(await pub.goLive())) {
        await onStop?.();
        return;
      }
      toast.success("You're live");
      onLive?.();
    } catch (e) {
      toast.error(e.message || 'Could not go live');
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    pub.stop();
    setBusy(true);
    try {
      await onStop?.();
      toast.success('Broadcast ended');
      onStopped?.();
    } catch (e) {
      toast.error(e.message || 'Could not end the broadcast');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="camera-broadcaster">
      <div className="relative aspect-[3/4] w-full bg-black rounded-xl overflow-hidden">
        <video ref={pub.attach} autoPlay playsInline muted
               className="w-full h-full object-cover"
               data-testid="camera-preview" />
        {!cameraOn && (
          <button onClick={async () => setCameraOn(await pub.openCamera())}
                  className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-400"
                  data-testid="open-camera-btn">
            <Camera className="h-8 w-8" />
            <span className="text-sm">Tap to turn on the camera</span>
          </button>
        )}
        {live && (
          <>
            <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-rose-600 px-2.5 py-1 text-xs font-semibold text-white">
              <span className="h-2 w-2 rounded-full bg-white animate-pulse" />LIVE
            </span>
            <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-black/60 px-2.5 py-1 text-xs text-white"
                  data-testid="viewer-count">
              <Users className="h-3 w-3" />{pub.viewers}
            </span>
          </>
        )}
        {cameraOn && (
          <button onClick={() => pub.openCamera(pub.facing === 'user' ? 'environment' : 'user')}
                  className="absolute bottom-3 right-3 rounded-full bg-black/60 p-2.5 text-white"
                  data-testid="flip-camera-btn">
            <SwitchCamera className="h-4 w-4" />
          </button>
        )}
      </div>

      <CameraTrouble error={pub.error} steps={pub.errorSteps}
                     onRetry={async () => setCameraOn(await pub.openCamera())} />

      {cameraOn && pub.hasAudio === false && (
        <p className="text-xs text-amber-400/90 text-center" data-testid="no-audio-note">
          The microphone is not available, so this will go out without sound.
        </p>
      )}

      {!live && (
        <div>
          <Label className="text-slate-300">{titleLabel}</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)}
                 placeholder={titlePlaceholder}
                 className="mt-1.5 bg-slate-950 border-slate-700"
                 data-testid="broadcast-title" />
        </div>
      )}

      {live ? (
        <Button variant="destructive" className="w-full h-14 text-base"
                disabled={busy} onClick={stop} data-testid="stop-broadcast-btn">
          <Square className="h-5 w-5 mr-2" />Stop the stream
        </Button>
      ) : (
        <>
          <Button variant="outline"
                  className="w-full h-11 border-slate-700 bg-transparent text-slate-200"
                  onClick={() => setChecking(true)} data-testid="preflight-btn">
            <Gauge className="h-4 w-4 mr-2" />Check camera and sound
          </Button>
          <Button className="w-full h-14 text-base bg-rose-600 hover:bg-rose-500"
                  disabled={busy || pub.status === 'starting'}
                  onClick={start} data-testid="start-broadcast-btn">
            {busy || pub.status === 'starting'
              ? <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              : <Radio className="h-5 w-5 mr-2" />}
            Start the stream
          </Button>
        </>
      )}

      <PreFlightCheck open={checking} onOpenChange={setChecking}
                      onPassed={() => pub.openCamera().then(setCameraOn)} />

      {note && (
        <p className="text-xs text-slate-500 text-center" data-testid="transport-note">
          {note}
        </p>
      )}
    </div>
  );
};

export default CameraBroadcaster;
